/**
 * Radix dialog primitives with an overlay, portal, responsive content frame, and accessible
 * title/description wrappers.
 *
 * The backdrop is a plain element rather than Radix's `Dialog.Overlay`. Radix wraps its overlay in
 * react-remove-scroll, which locks page scrolling by setting an inherited custom property on
 * `<body>`; every element on the page then has its style recalculated when a dialog opens and
 * again when it closes. On the 100-card activity board that was two recalculations of about 8,600
 * elements, roughly 90 ms, inside the click that opens the editor. The lock had nothing to do:
 * `index.css` gives `body` `overflow: hidden` and a fixed viewport height on every page, so the
 * document itself never scrolls, and the backdrop covers the scrolling regions inside it. See
 * docs/developer/ui-latency.md.
 *
 * @module apps/web/src/components/ui/dialog
 */

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Whether the nearest dialog is open; read by `DialogOverlay` to run its enter and exit fades. */
const DialogOpenContext = React.createContext(false);

/**
 * Radix dialog root managing controlled or uncontrolled open state. It also publishes the open
 * flag to `DialogOverlay`, which is not a Radix part and cannot read Radix's own context.
 */
function Dialog({
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false);
  const open = openProp ?? uncontrolledOpen;
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (openProp === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [openProp, onOpenChange]
  );
  return (
    <DialogOpenContext.Provider value={open}>
      <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange} {...props} />
    </DialogOpenContext.Provider>
  );
}
/**
 * Radix trigger that opens the nearest dialog.
 */
const DialogTrigger = DialogPrimitive.Trigger;
/**
 * Radix portal mounting dialog content outside its parent layout.
 */
const DialogPortal = DialogPrimitive.Portal;
/**
 * Radix close trigger for the nearest dialog.
 */
const DialogClose = DialogPrimitive.Close;

/**
 * Ref-forwarding animated dialog backdrop. Mounted by `DialogPortal` only while its dialog is open
 * or fading out; it blocks pointer input to the page behind it, and a press on it reaches the
 * dialog content's outside-press handling, which closes the dialog. `data-dialog-overlay` lets
 * `index.css` keep the page's pointer-events explicit while the backdrop is up. `will-change`
 * keeps the backdrop on its own compositor layer, so its fade can run without repainting the page
 * beneath it.
 */
const DialogOverlay = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const open = React.useContext(DialogOpenContext);
    return (
      <div
        ref={ref}
        data-state={open ? 'open' : 'closed'}
        data-dialog-overlay=""
        className={cn(
          'pointer-events-auto fixed inset-0 z-50 bg-black/80 [will-change:opacity] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          className
        )}
        {...props}
      />
    );
  }
);
DialogOverlay.displayName = 'DialogOverlay';

/**
 * Portal-mounted dialog content with overlay and a built-in accessible close button.
 */
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 grid max-h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto rounded-lg border bg-background p-4 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:p-6',
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-[calc(1rem+env(safe-area-inset-top))] rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground sm:top-4">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

/**
 * Responsive wrapper for dialog title and description.
 */
const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

/**
 * Responsive action row at the bottom of a dialog.
 */
const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

/**
 * Ref-forwarding accessible Radix dialog title with heading typography.
 */
const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

/**
 * Ref-forwarding accessible Radix dialog description with muted typography.
 */
const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
